import Styles from './CalendarEventCard.module.scss'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'
import cx from 'classnames'
import { EmptyDocIcon } from './EmptyDocIcon'
import { EmptyDesignIcon } from './EmptyDesignIcon'
import { LoadingColorState } from './LoadingColorState'
import { StoreSnapshot } from '@tldraw/tldraw'
import { DesignContentPreviewResizeWrapper } from 'components/DesignContentPreviewResizeWrapper/DesignContentPreviewResizeWrapper'
import { ComponentType, RefObject, useRef } from 'react'
import { ImageWrapper } from './ImageWrapper'
import { Text } from 'blaze-ui/Text/Text'
import { GenerationFailedPreview } from './GenerationFailedPreview/GenerationFailedPreview'
import { reportError } from 'utils/error'
import { ImagePreviewSource, usePreviewImageState } from 'hooks/calendar/usePreviewImageState'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Carousel } from 'blaze-ui/icons/20'
import { VideoOn } from 'blaze-ui/icons'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { PollDesignReplacement } from 'screens/Calendar/FullScreenPreview/EditPostMenu/PollDesignReplacement'
import { isArticle } from 'screens/ScheduledPosts/utils'
import { ChannelType } from 'components/CampaignCreationModal/Types'

interface ImageProps {
  event: AlmanacFileCalendarEvent
  loadingColor: string
  loadingProgress: number
  stepLabel?: string | null
  stepIcon?: ComponentType<{ size?: number; color?: string }>
  referenceImageUrl?: string | null
  imageLoaded: boolean
  onImageLoaded: (value: boolean) => void
  containerRef: RefObject<HTMLDivElement>
  cardWidth: number
  previewWidth?: number
  designLoaded: boolean
  onDesignLoaded: (value: boolean) => void
  maxDocPreviewLines?: number
  allowExpandCaption?: boolean
  didGenerationFail?: boolean
  isCopilot?: boolean
  generationStartedAt?: string | null
  showRenderingImageOverlay?: boolean
}

export const Image = observer(
  ({
    event,
    loadingColor,
    imageLoaded,
    onImageLoaded,
    loadingProgress,
    stepLabel,
    stepIcon,
    referenceImageUrl,
    containerRef,
    cardWidth,
    previewWidth,
    designLoaded,
    onDesignLoaded,
    maxDocPreviewLines,
    allowExpandCaption = true,
    didGenerationFail = false,
    isCopilot = false,
    generationStartedAt,
    showRenderingImageOverlay = false,
  }: ImageProps) => {
    const { generationTimestampStore } = useRootStore()
    const imageWrapperRef = useRef<HTMLDivElement>(null)
    const { asset_type, image_url: imageUrl, design_content, width, height, content } = event

    const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(event.key)
    const startedReplacingAt = generationTimestampStore.getStartedAt(event.key)
    const replacingImageUrl = generationTimestampStore.getReplacingImageUrl(event.key)
    const isReplacingFrame = !!replaceFrameBatchId

    const calendarV2 = useFlipperFlag('calendar_v2')
    const { previewSource, showLoading } = usePreviewImageState({
      event,
      isImageLoaded: imageLoaded,
      isDesignLoaded: designLoaded,
      didGenerationFail,
      isCopilot,
      calendarV2,
      showRenderingImageOverlay,
    })

    const containerHeight = imageWrapperRef.current?.getBoundingClientRect().height || 200
    const maxDocLines = maxDocPreviewLines || Math.floor(containerHeight / 15)

    if (previewSource === ImagePreviewSource.FAIL) {
      return (
        <ImageWrapper event={event} loadingColor={loadingColor} cardWidth={cardWidth}>
          <GenerationFailedPreview
            fileId={event.key}
            variant="eventCard"
            channel={event.channel}
            contentType={event.content_type}
          />
        </ImageWrapper>
      )
    }

    if (previewSource === ImagePreviewSource.NONE) return null

    // If the image url is present
    // - if it has not been loaded into the browser and the design content has not been rendered, show the loading state
    //   and the image hidden so it loads in the background
    // - if it has been loaded into the browser or the design content has been rendered, show the image
    //
    // NOTE: We need to check for the design content having been rendered already because if it has, we don't want to show the
    //       loading state AND THEN the image, it should be a seamless transition from the design content to the image.
    if (previewSource === ImagePreviewSource.IMAGE_URL) {
      if (showLoading || isReplacingFrame) {
        return (
          <ImageWrapper
            event={event}
            loadingColor={loadingColor}
            cardWidth={cardWidth}
            isLoading
            allowExpand={allowExpandCaption}
          >
            <LoadingColorState
              loadingColor={loadingColor}
              progress={loadingProgress}
              stepLabel={isReplacingFrame ? 'Replacing design...' : stepLabel}
              stepIcon={isReplacingFrame ? undefined : stepIcon}
              referenceImageUrl={replacingImageUrl || referenceImageUrl}
              generationStartedAt={startedReplacingAt || generationStartedAt}
            />
            <img
              src={imageUrl}
              className={cx(Styles.contentImage, Styles.hidden)}
              alt="event"
              onLoad={() => onImageLoaded(true)}
              onLoadCapture={() => onImageLoaded(true)}
              onError={(err) => reportError(err)}
            />
            <PollDesignReplacement designKey={event.key} onComplete={() => onImageLoaded(true)} />
          </ImageWrapper>
        )
      }

      return (
        <ImageWrapper
          event={event}
          loadingColor={loadingColor}
          cardWidth={cardWidth}
          showCaption
          allowExpand={allowExpandCaption}
        >
          {event.is_carousel && (
            <div className={Styles.carouselIcon}>
              <Carousel color="var(--light-100)" />
            </div>
          )}
          <img src={imageUrl} className={Styles.contentImage} alt="event" />
        </ImageWrapper>
      )
    }

    if (previewSource === ImagePreviewSource.DESIGN) {
      const overlayVisible = calendarV2 ? showLoading : showLoading || !designLoaded
      return (
        <ImageWrapper
          event={event}
          loadingColor={loadingColor}
          cardWidth={cardWidth}
          showCaption={!overlayVisible}
          isLoading={overlayVisible}
          allowExpand={allowExpandCaption}
        >
          {overlayVisible && (
            <LoadingColorState
              loadingColor={loadingColor}
              progress={loadingProgress}
              stepLabel={stepLabel}
              stepIcon={stepIcon}
              referenceImageUrl={referenceImageUrl}
              generationStartedAt={startedReplacingAt || generationStartedAt}
            />
          )}
          {event.is_carousel && !overlayVisible && (
            <div className={Styles.carouselIcon}>
              <Carousel color="var(--light-100)" />
            </div>
          )}
          <DesignContentPreviewResizeWrapper
            width={width ?? 0}
            height={height ?? 0}
            designContent={design_content as StoreSnapshot<any>}
            containerRef={containerRef}
            containerWidth={previewWidth}
            className={cx({ [Styles.hidden]: overlayVisible })}
            onLoadingStateChange={(status) => onDesignLoaded(status === 'synced-local')}
            autoplayVideo={false}
          />
        </ImageWrapper>
      )
    }

    // If the content is a doc and its preview image failed to generate, show the doc's plain text
    if (previewSource === ImagePreviewSource.TEXT) {
      return (
        <ImageWrapper
          event={event}
          loadingColor={loadingColor}
          cardWidth={cardWidth}
          ref={imageWrapperRef}
          allowExpand={allowExpandCaption}
        >
          <div className={Styles.eventTextContainer}>
            <Text lineClamp={maxDocLines}>{content}</Text>
          </div>
        </ImageWrapper>
      )
    }

    // If the content is being generated, show the loading state
    if (previewSource === ImagePreviewSource.LOADING) {
      const variant = event.channel === ChannelType.Newsletter ? 'newsletter' : 'blog'
      const shouldUseVariant = isArticle({ almanac_file: event })

      return (
        <ImageWrapper
          event={event}
          loadingColor={loadingColor}
          cardWidth={cardWidth}
          isLoading
          allowExpand={allowExpandCaption}
        >
          <LoadingColorState
            variant={shouldUseVariant ? variant : undefined}
            loadingColor={loadingColor}
            progress={loadingProgress}
            stepLabel={stepLabel}
            stepIcon={stepIcon}
            referenceImageUrl={referenceImageUrl}
            generationStartedAt={startedReplacingAt || generationStartedAt}
          />
        </ImageWrapper>
      )
    }

    // If the content is empty, show the empty state
    return (
      <ImageWrapper
        event={event}
        loadingColor={loadingColor}
        cardWidth={cardWidth}
        showCaption
        allowExpand={allowExpandCaption}
      >
        {asset_type === 'Design' && (
          <div className={Styles.emptyContainer} data-testid="empty-design-container">
            <EmptyDesignIcon />
          </div>
        )}
        {asset_type === 'Article' && (
          <div className={Styles.emptyContainer} data-testid="empty-doc-container">
            <EmptyDocIcon />
          </div>
        )}
        {asset_type === 'Video' && (
          <div className={Styles.emptyContainer} data-testid="empty-video-container">
            <VideoOn size={40} color="var(--dark-15)" />
          </div>
        )}
      </ImageWrapper>
    )
  },
)
